import React, { useEffect, useState } from "react";

import PaymentForm from "./PaymentForm";
import KanopyPaymentForm from "./KanopyPaymentForm";
import BillingAddressForm from "./BillingAddress";
import { isKanopyGateway, useOnlineOrderSettings, getKanopyDeviceGuid, getKanopyApiKey } from "contaxt/OnlineOrderSettingsContext";
import {
  FieldErrors,
  UseFormSetValue,
  UseFormClearErrors,
  Control,
  UseFormHandleSubmit,
} from "react-hook-form";
import { CardsData } from "./CheckoutPage";
import Card from "shared/Card";
import { PaymentFormSkeleton } from "shared/OnlineOrderingSkeletons";


interface CardData {
  payment: {
    creditCard: {
      cardNumber: string;
      cardType: string;
      expirationDate?: string;
    };
  };
  billTo: {
    customerId?: string;
    firstName: string;
    lastName: string;
    profileId: string;
    address: string;
    city: string;
    state: string;
    zip: string;
  };
  customerPaymentProfileId: string;
}

export type CheckoutFormValues = {
  delivery: {
    deliverTo: string;
    contact: string;
    address1: string;
    address2: string;
    city: string;
    state: string;
    country: string;
    zip: string;
    deliveryInstructions: string;
  };
  cardNumber: string;
  firstname: string;
  lastname: string;
  expirationDate: string;
  CVC: string;
  payment: {
    address1: string;
    address2: string;
    city: string;
    state: string;
    country: string;
    zip: string;
  };
};

export interface PaymentSectionProps {
  auth: CardData[];
  selectedCard: CardData | null;
  control: Control<CheckoutFormValues>;
  errors: FieldErrors<CheckoutFormValues>;
  setValue: UseFormSetValue<CheckoutFormValues>;
  clearErrors: UseFormClearErrors<CheckoutFormValues>;
  handleSubmit: UseFormHandleSubmit<CheckoutFormValues>;
  userDataString: string | undefined;
  setSelectedCard: (card: CardData | null) => void;
  saveCard: boolean;
  setSaveCard: (saveCard: boolean) => void;
  setCarddata: (cardData: CardsData | null) => void;
}

const PaymentMethod: React.FC<PaymentSectionProps> = ({
  auth,
  selectedCard,
  control,
  errors,
  setValue,
  clearErrors,
  handleSubmit,
  userDataString,
  setSelectedCard,
  saveCard,
  setSaveCard,
  setCarddata,
}) => {
  const { isLoading: settingsLoading } = useOnlineOrderSettings();
  // Read from sessionStorage after the context finishes loading — the re-render on
  // settingsLoading → false is what triggers this and ensures the values are populated.
  // (Kanopy fields may come from the top-level response, not onlineOrderSettings, so we
  // use the sessionStorage helpers rather than settings?.kanopyDeviceGuid.)
  const kanopyDeviceGuid = !settingsLoading ? getKanopyDeviceGuid() : "";
  const kanopyApiKey = !settingsLoading ? getKanopyApiKey() : "";
  // Only render KanopyPaymentForm once settings have loaded and credentials are available
  const kanopyReady = !settingsLoading && !!kanopyDeviceGuid && !!kanopyApiKey;

  const [billingCity, setBillingCity] = useState("");
  const [billingAddressInfo, setBillingAddressInfo] = useState("");
  const [billingZip, setBillingZip] = useState("");
  const [billingState, setBillingState] = useState("");
  const [billingCountry, setBillingCountry] = useState("");
  const [showCardField, setShowCardField] = useState(false);
  const defaultCheckedCard = auth.slice(-1)[0];
  useEffect(() => {
    const init = async () => {
      await setSelectedCard(defaultCheckedCard);
    };
    init();
  }, [defaultCheckedCard]);

  const handleSaveCard = async (data: any) => {
    if (saveCard === true) {
      setSaveCard(false);
    } else {
      setSaveCard(true);
    }
    setCarddata(data);
    setSelectedCard(null);
  };
  const handleAddCard = () => {
    // Add logic to handle adding a new card here
    setSelectedCard(null);
    setShowCardField(true);
  };

  const handleSelect = (card: any) => {
    setSelectedCard(card);
    setShowCardField(false);
  };

  return (
    <div className="flex-1 min-w-0">
      <div className="rounded-xl border border-slate-200 dark:border-slate-700 bg-slate-50/90 dark:bg-slate-800/50 p-4 sm:p-5 lg:p-6 space-y-1">
        <h2 className="text-base sm:text-lg font-semibold text-slate-900 dark:text-slate-100 mb-3 lg:hidden">
          Payment
        </h2>
        <p className="text-xs text-slate-500 dark:text-slate-400 mb-4 lg:hidden">
          Card and billing details. Your order is summarized above.
        </p>
        <div id="PaymentMethod" className="scroll-mt-24">
          <div className="flex items-start space-x-3 sm:space-x-6">
            <div className="flex-1">
              {auth?.length > 0 && (
                <>
                  <label
                    htmlFor="Credit-Card"
                    className="flex items-center space-x-4 sm:space-x-6"
                  >
                    <div className="p-2.5 rounded-xl border-2 border-slate-200 dark:border-slate-600 text-slate-800 dark:text-slate-200">
                      <svg
                        className="w-6 h-6 sm:w-7 sm:h-7"
                        viewBox="0 0 24 24"
                        fill="none"
                      >
                        <path
                          d="M2 12.6101H19"
                          stroke="currentColor"
                          strokeWidth="1.5"
                          strokeMiterlimit="10"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                        <path
                          d="M19 10.28V17.43C18.97 20.28 18.19 21 15.22 21H5.78003C2.76003 21 2 20.2501 2 17.2701V10.28C2 7.58005 2.63 6.71005 5 6.57005C5.24 6.56005 5.50003 6.55005 5.78003 6.55005H15.22C18.24 6.55005 19 7.30005 19 10.28Z"
                          stroke="currentColor"
                          strokeWidth="1.5"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                        <path
                          d="M22 6.73V13.72C22 16.42 21.37 17.29 19 17.43V10.28C19 7.3 18.24 6.55 15.22 6.55H5.78003C5.50003 6.55 5.24 6.56 5 6.57C5.03 3.72 5.81003 3 8.78003 3H18.22C21.24 3 22 3.75 22 6.73Z"
                          stroke="currentColor"
                          strokeWidth="1.5"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                        <path
                          d="M5.25 17.8101H6.96997"
                          stroke="currentColor"
                          strokeWidth="1.5"
                          strokeMiterlimit="10"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                        <path
                          d="M9.10986 17.8101H12.5499"
                          stroke="currentColor"
                          strokeWidth="1.5"
                          strokeMiterlimit="10"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    </div>
                    <p className="font-medium text-slate-900 dark:text-slate-100">
                      Debit / Credit Card
                    </p>
                  </label>
                </>
              )}

              <div className="mb-4 space-y-3 sm:space-y-5 max-lg:pb-[max(1.5rem,calc(env(safe-area-inset-bottom,0px)+7rem))]">
                {auth?.length > 0 && (
                  <div className="p-4 mb-3">
                    {!showCardField && (
                      <button
                        type="button"
                        onClick={handleAddCard}
                        className="lg:hidden mb-4 mx-auto flex w-full max-w-[min(16rem,86vw)] sm:max-w-[min(18rem,80vw)] md:max-w-[min(20rem,72vw)] min-h-[40px] items-center justify-center rounded-lg bg-custom-green px-3 py-2 text-sm font-semibold text-white shadow-sm touch-manipulation active:opacity-90 sm:min-h-[42px] sm:px-4 sm:py-2.5 sm:text-base md:min-h-[44px]"
                      >
                        Add card
                      </button>
                    )}
                    {auth.map((card, index) => (
                    <Card
                        key={index}
                        card={card}
                        onSelect={handleSelect}
                        selected={
                          selectedCard &&
                          selectedCard.payment.creditCard.cardNumber.replace(
                            /\s/g,
                            ""
                          ) ===
                            card.payment.creditCard.cardNumber.replace(
                              /\s/g,
                              ""
                            )
                        }
                        defaulChecked={
                          defaultCheckedCard.payment.creditCard.cardNumber ===
                          card.payment.creditCard.cardNumber.replace(/\s/g, "")
                        }
                        cardType={card.payment.creditCard.cardType}
                        cardHolderName={`${card.billTo.firstName} ${card.billTo.lastName}`}
                        cardNumber={card.payment.creditCard.cardNumber.replace(
                          /\s/g,
                          ""
                        )}
                        onEdit={() => {}}
                        onDelete={() => {}}
                      />
                    ))}
                    {!showCardField && (
                      <button
                        type="button"
                        onClick={handleAddCard}
                        className="mt-2 hidden bg-custom-green px-4 py-2 font-normal text-white rounded lg:inline-block min-h-[44px]"
                      >
                        Add Card
                      </button>
                    )}
                  </div>
                )}
                {showCardField && (
                  <form>
                    {isKanopyGateway() ? (
                      kanopyReady ? (
                        <KanopyPaymentForm deviceGuid={kanopyDeviceGuid} apiKey={kanopyApiKey} />
                      ) : (
                        <PaymentFormSkeleton className="mt-2" />
                      )
                    ) : (
                      <PaymentForm
                        control={control} // Pass your form control
                        errors={errors} // Pass your form errors
                        handleAddCard={handleAddCard} // Pass your handleAddCard function
                        setSelectedCard={setSelectedCard}
                      />
                    )}
                    <BillingAddressForm
                      control={control}
                      errors={errors}
                      setValue={setValue}
                      clearErrors={clearErrors}
                      namespace="payment"
                      billingCity={billingCity}
                      billingAddressInfo={billingAddressInfo}
                      billingZip={billingZip}
                      billingState={billingState}
                      billingCountry={billingCountry}
                    />

                    <div className="mt-6 flex items-center">
                      <input
                        type="checkbox"
                        id="saveForFuturePayments"
                        className="h-4 w-4 text-blue-500 dark:bg-slate-800 dark:focus:ring-blue-400 border-slate-300 dark:border-slate-600 rounded focus:ring-blue-400"
                        onChange={() => {
                          handleSubmit(handleSaveCard)();
                        }}
                      />
                      <label
                        htmlFor="saveForFuturePayments"
                        className="ml-2 text-sm font-medium text-slate-700 dark:text-slate-200"
                      >
                        Save for future payments
                      </label>
                    </div>
                  </form>
                )}
                {!auth.length && (
                  <>
                    {isKanopyGateway() ? (
                      kanopyReady ? (
                        <KanopyPaymentForm deviceGuid={kanopyDeviceGuid} apiKey={kanopyApiKey} />
                      ) : (
                        <PaymentFormSkeleton className="mt-2" />
                      )
                    ) : (
                      <PaymentForm
                        control={control}
                        errors={errors}
                        handleAddCard={handleAddCard} // Pass your handleAddCard function
                      />
                    )}
                    <BillingAddressForm
                      control={control}
                      errors={errors}
                      setValue={setValue}
                      clearErrors={clearErrors}
                      namespace="payment"
                      billingCity={billingCity}
                      billingAddressInfo={billingAddressInfo}
                      billingZip={billingZip}
                      billingState={billingState}
                      billingCountry={billingCountry}
                    />
                    {userDataString && (
                      <div className="mt-6 flex items-center">
                        <input
                          type="checkbox"
                          id="saveForFuturePayments"
                          className="h-4 w-4 text-blue-500 border-slate-300 dark:border-slate-600 rounded focus:ring-blue-400 dark:bg-slate-800 dark:focus:ring-blue-400"
                          onChange={() => {
                            handleSubmit(handleSaveCard)();
                          }}
                        />
                        <label
                          htmlFor="saveForFuturePayments"
                          className="ml-2 text-sm font-medium text-slate-700 dark:text-slate-200"
                        >
                          Save for future payments
                        </label>
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PaymentMethod;
